import { has } from 'lodash-es'

/**
 * Customer Return Payload Builder Composable
 *
 * Builds API payloads for return submission by mapping frontend data
 * structures to the backend API format.
 */

/**
 * @typedef {Object} ProcessingItem
 * @property {string} lineItemSessionId - Backend session ID for this item
 * @property {Array<{id: string}>} images - Array of image objects with IDs
 * @property {string} id - Frontend item ID
 */

/**
 * @typedef {Object} WrongItemData
 * @property {Object|null} selectedProduct - Product that was actually received
 * @property {Object} capturedImage - Image of the wrong item
 * @property {string} [capturedImage.id] - Image ID in backend
 * @property {boolean} verificationSkipped - Whether user skipped identification
 * @property {string|null} skippedReason - Reason for skipping (optional)
 */

/**
 * @typedef {Object} SubmissionPayloadItem
 * @property {string} returnSessionItemId - Backend session item ID
 * @property {string[]} images - Array of image IDs
 * @property {Object|null} selectedWrongItem - Wrong item product data or null
 */

/**
 * Composable for building return submission payloads
 * @returns {Object} Payload builder functions
 */
export function useCustomerReturnPayloadBuilder() {
  /**
   * Extract image IDs from an item's images array
   * @param {ProcessingItem} item - Item with images
   * @returns {string[]} Array of image IDs
   */
  function mapItemImages(item) {
    if (!item.images || !Array.isArray(item.images)) {
      return []
    }

    return item.images.map(img => img.id).filter(id => id != null)
  }

  /**
   * Extract wrong item data for an item if it exists
   * @param {ProcessingItem} item - Item being processed
   * @param {Object} wrongItemsById - Map of item IDs to wrong item data
   * @returns {{ images: string[], selectedProduct: Object|null, verificationSkipped: boolean, skippedReason: string|null }} Wrong item data
   */
  function mapWrongItemData(item, wrongItemsById) {
    if (!has(wrongItemsById, item.id)) {
      return {
        images: [],
        selectedProduct: null,
        verificationSkipped: false,
        skippedReason: null,
      }
    }

    const wrongItemData = wrongItemsById[item.id]

    // Include the captured wrong item image if it exists
    const wrongItemImages = []
    if (wrongItemData?.capturedImage?.id) {
      wrongItemImages.push(wrongItemData.capturedImage.id)
    }

    return {
      images: wrongItemImages,
      selectedProduct: wrongItemData.selectedProduct || null,
      verificationSkipped: wrongItemData.verificationSkipped || false,
      skippedReason: wrongItemData.skippedReason || null,
    }
  }

  /**
   * Extract packaging photo IDs from item's packaging assessment data
   * @param {Object} item - Processing item with packagingAssessment
   * @returns {string[]} Array of unique packaging photo IDs for this item
   */
  function extractPackagingPhotoIds(item) {
    const photoIds = []

    const packagingAssessment = item.packagingAssessment
    if (packagingAssessment?.boxes && Array.isArray(packagingAssessment.boxes)) {
      packagingAssessment.boxes.forEach(box => {
        if (box.dimensionsPhotoId) photoIds.push(box.dimensionsPhotoId)
      })
    }

    return [...new Set(photoIds)] // Remove duplicates
  }

  /**
   * Map a single processing item to submission payload format
   * @param {ProcessingItem} item - Item to map (with packagingAssessment and productStateAssessment)
   * @param {Object} wrongItemsById - Map of item IDs to wrong item data
   * @returns {SubmissionPayloadItem} Mapped payload item
   */
  function mapItemToPayload(item, wrongItemsById = {}) {
    const itemImages = mapItemImages(item)
    const wrongItemData = mapWrongItemData(item, wrongItemsById)
    const packagingPhotoIds = extractPackagingPhotoIds(item)

    return {
      returnSessionItemId: item.lineItemSessionId,
      images: [...itemImages, ...wrongItemData.images, ...packagingPhotoIds],
      selectedWrongItem: wrongItemData.selectedProduct,
    }
  }

  /**
   * Build complete submission payload from items to process
   * Each item now includes its own packagingAssessment and productStateAssessment
   * @param {ProcessingItem[]} itemsToProcess - Array of items being returned with assessments
   * @param {Object} wrongItemsById - Map of item IDs to wrong item data
   * @returns {SubmissionPayloadItem[]} Array of payload items ready for API
   */
  function buildSubmissionPayload(itemsToProcess, wrongItemsById = {}) {
    if (!Array.isArray(itemsToProcess)) {
      return []
    }

    return itemsToProcess.map(item => mapItemToPayload(item, wrongItemsById))
  }

  /**
   * Validate that a payload is ready for submission
   * @param {SubmissionPayloadItem[]} payload - Payload to validate
   * @returns {{ valid: boolean, errors: string[] }} Validation result
   */
  function validatePayload(payload) {
    const errors = []

    if (!Array.isArray(payload)) {
      errors.push('Payload must be an array')
      return { valid: false, errors }
    }

    if (payload.length === 0) {
      errors.push('Payload must contain at least one item')
      return { valid: false, errors }
    }

    payload.forEach((item, index) => {
      if (!item.returnSessionItemId) {
        errors.push(`Item ${index + 1}: Missing returnSessionItemId`)
      }

      if (!Array.isArray(item.images)) {
        errors.push(`Item ${index + 1}: Images must be an array`)
      }
    })

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  return {
    buildSubmissionPayload,
    mapItemImages,
    mapItemToPayload,
    mapWrongItemData,
    extractPackagingPhotoIds,
    validatePayload,
  }
}
